import { Component, DestroyRef, inject, Input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { requireScopedProject } from "@domain/project/project-deserializer";
import { ProjectFloorplanData, ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import type { ScopedProject } from "@domain/project/project-guards";
import { SyncState } from "@domain/project/project-image.service";
import { FormDirective, FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { SelectComponent, SelectOptionComponent } from "@odx/angular/components/select";
import { pairwise, startWith } from "rxjs";

@Component({
  selector: "app-floorplan-selector",
  imports: [
    FormDirective,
    FormFieldComponent,
    FormFieldControlDirective,
    ReactiveFormsModule,
    SelectComponent,
    SelectOptionComponent,
  ],
  templateUrl: "./floorplan-selector.component.html",
  styleUrl: "./floorplan-selector.component.scss",
})
export class FloorplanSelectorComponent implements OnInit {
  private router = inject(Router);
  private projectFloorplanService = inject(ProjectFloorplanService);
  private destroyRef = inject(DestroyRef);
  private suppressNextRecoveryEmission = false;

  @Input({ required: true }) set activeFloorplan(floorplan: Floorplan) {
    this.floorplanForm.patchValue({ activeFloorplan: floorplan }, { emitEvent: false });
    this.project = requireScopedProject(floorplan.project);
  }

  protected floorplanForm: FormGroup;
  protected project!: ScopedProject;
  floorplans: ProjectFloorplanData[] = [];

  constructor() {
    const formBuilder = inject(FormBuilder);
    this.floorplanForm = formBuilder.group({
      activeFloorplan: [],
    });
  }

  ngOnInit(): void {
    this.floorplans = this.projectFloorplanService.floorplans.map((floorplan) => ({
      floorplan,
      projectId: this.project.id,
      syncState: SyncState.SYNCED,
    }));

    const activeFloorplanControl = this.floorplanForm.controls["activeFloorplan"];

    activeFloorplanControl.valueChanges
      .pipe(startWith(this.activeFloorplan), pairwise(), takeUntilDestroyed(this.destroyRef))
      .subscribe(([previousFloorplan, currentFloorplan]: [Floorplan, Floorplan]) => {
        if (!currentFloorplan?.id) {
          if (previousFloorplan?.id) {
            this.suppressNextRecoveryEmission = true;
            activeFloorplanControl.patchValue(previousFloorplan, { emitEvent: false });
          }
          return;
        }

        if (this.suppressNextRecoveryEmission) {
          this.suppressNextRecoveryEmission = false;
          return;
        }

        if (previousFloorplan?.id === currentFloorplan.id) {
          return;
        }

        this.router.navigate(["/projects", this.project.id, "floorplans", currentFloorplan.id]);
      });
  }

  get activeFloorplan(): Floorplan {
    return this.floorplanForm.value.activeFloorplan as Floorplan;
  }

  getFloorplanName(floorplan: Floorplan) {
    return floorplan.name.length > 40 ? floorplan.name.substring(0, 40 - 3) + "..." : floorplan.name;
  }
}
